import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Store as StoreIcon,
    MapPin,
    Calendar,
    Users,
    Coins,
    DollarSign,
    AlertTriangle,
    Info,
    User,
    Mail,
    Phone,
    Banknote,
    PlusCircle,
    ChevronDown
} from 'lucide-react';
import { useToast } from "../hooks/useToast";
import { currencyCodeToSymbolMap } from '../apiHelper';
import Dropdown from "../components/ui/Dropdown";
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { DataTable2 } from '../components/ui/DataTable2';

/**
 * StoreDetails Component
 *
 * This component displays detailed information for a single branch, including Store & Owner Info, Staff, and Key Metrics.
 */
const StoreDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [branch, setBranch] = useState(location.state?.branch);
    const [activeTab, setActiveTab] = useState('store');

    // Show toast on initial load
    useEffect(() => {
        if (branch) {
            // console.log("Branch object received from Stores component:", branch);
            showToast(`Displaying details for branch: ${branch.identifier}`, 'success');
        } else {
            console.error("No branch object found in location state.");
            showToast("Failed to load branch details. No data provided.", 'error');
        }
    }, [branch, showToast]);

    // Handler for the "Sell from this store" button
    const handleSellClick = () => {
        if (branch && branch.store_id) {
            navigate('/pos', { state: { storeId: branch.store_id, storeName: branch.identifier } });
        } else {
            showToast("Could not find a store ID to begin selling.", 'error');
        }
    };

    // Handler for the "Customer return goods" action
    const handleCustomerReturnClick = () => {
        if (branch && branch.store_id) {
            navigate('/branches/customer-return', { state: { storeId: branch.store_id, storeName: branch.identifier } });
        } else {
            showToast("Could not find a store ID to process a return.", 'error');
        }
    };

    // Handler for the Define taxes button
    const handleDefineBranchTaxes = () => {
        if (branch && branch.store_id) {
            navigate('/branches/spec-branch-tax', { state: { storeId: branch.store_id } });
        } else {
            showToast("Could not find a branch ID to process tax specification", "error");
        }
    };

    // Handler for the Manage Discounts button
    const handleNavigateDiscount = () => {
        if (branch && branch.store_id) {
            navigate('/branches/manage-discount', { state: { storeId: branch.store_id } });
        } else {
            showToast("Could not find a branch ID to process discount management", "error");
        }
    };

    // Handler for the Inventory Transfer button
    const handleInventoryTransferClick = () => {
        if (branch && branch.store_id) {
            navigate('/inventory/inventory-transfer', { state: { storeId: branch.store_id, storeName: branch.identifier } });
        } else {
            showToast("Could not find a store initiate inventory transfer.", "error");
        }
    };

    // Key Metrics
    const storeInfoCards = useMemo(() => {
        if (!branch) return [];
        const currencySymbol = currencyCodeToSymbolMap[branch.setccurr] || branch.setccurr;

        const totalRevenue = branch.orders?.reduce((sum, order) => sum + (order.totalproduct || 0), 0) || 0;

        return [
            {
                title: "Total Inventory Value",
                value: `${currencySymbol}${branch.totalInventoryValue?.toFixed(2) || '0.00'}`,
                icon: <Coins size={24} className="text-purple-600" />,
                description: "Sum of all inventory lot costs.",
            },
            {
                title: "Total Revenue",
                value: `${currencySymbol}${totalRevenue?.toFixed(2) || '0.00'}`,
                icon: <DollarSign size={24} className="text-green-600" />,
                description: "Aggregated revenue from all orders.",
            },
            {
                title: "Active Staff",
                value: branch.staff?.length || 0,
                icon: <Users size={24} className="text-blue-600" />,
                description: "Number of staff assigned to this branch.",
            },
            {
                title: "Last Order Date",
                value: branch.orders?.length > 0
                    ? new Date(branch.orders[0].placed_on).toLocaleDateString()
                    : 'N/A',
                icon: <Calendar size={24} className="text-gray-600" />,
                description: "The date of the most recent sale.",
            },
        ];
    }, [branch]);

    // Staff Columns
    const staffColumns = useMemo(() => [
        {
            key: 'first_name',
            label: 'First Name',
            render: (value, row) => row.user?.first_name || 'N/A',
            sortable: true,
        },
        {
            key: 'last_name',
            label: 'Last Name',
            render: (value, row) => row.user?.last_name || 'N/A',
            sortable: true,
        },
        {
            key: 'logon_id',
            label: 'Email',
            render: (value, row) => row.user?.credential?.logon_id || 'N/A',
            sortable: true,
        },
        {
            key: 'member_type',
            label: 'Role',
            render: (value) => (
                <span className="capitalize">{value === 'O' ? 'Owner' : 'Staff'}</span>
            ),
            sortable: true,
        },
    ], []);

    // Conditional rendering if no branch data is available
    if (!branch) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <AlertTriangle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: No branch data could be loaded.</p>
                <Button onClick={() => window.history.back()}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            {/* Header Section with Branch Name and Back Button */}
            <div className="flex justify-between items-start flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <StoreIcon size={40} className="text-blue-600" />
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary">
                            {branch.identifier || 'Branch Details'}
                        </h1>
                        <p className="text-md text-gray-500 flex items-center gap-1">
                            <MapPin size={16} />
                            {branch.location || 'N/A'}
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Dropdown
                        align="right"
                        trigger={
                            <Button
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                <PlusCircle size={20} />
                                Actions
                                <ChevronDown size={16} />
                            </Button>
                        }
                    >
                        <div className="flex flex-col">
                            <Button
                                variant="ghost"
                                className="w-full text-left justify-start px-4 py-2 hover:bg-gray-100 text-text-primary"
                                onClick={handleSellClick}
                            >
                                Sell from this store
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full text-left justify-start px-4 py-2 hover:bg-gray-100 text-text-primary"
                                onClick={handleCustomerReturnClick}
                            >
                                Customer returns
                            </Button>
                            {/* <Button
                                variant="ghost"
                                className="w-full text-left justify-start px-4 py-2 hover:bg-gray-100 text-text-primary"
                                onClick={handleDefineBranchTaxes}
                            >
                                Define branch taxes
                            </Button> */}
                            <Button
                                variant="ghost"
                                className="w-full text-left justify-start px-4 py-2 hover:bg-gray-100 text-text-primary"
                                onClick={handleNavigateDiscount}
                            >
                                Manage Discounts
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full text-left justify-start px-4 py-2 hover:bg-gray-100 text-text-primary"
                                onClick={handleInventoryTransferClick}
                            >
                                Inventory transfer
                            </Button>
                        </div>
                    </Dropdown>
                    <Button onClick={() => window.history.back()} variant="secondary">
                        Go Back
                    </Button>
                </div>
            </div>

            {/* Key Metrics Section */}
            <Card className="p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-700">Key Metrics</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {storeInfoCards.map((card, index) => (
                        <div key={index} className="flex flex-col items-center p-4 border rounded-lg bg-gray-50 shadow-sm">
                            <div className="flex-shrink-0 mb-2">{card.icon}</div>
                            <p className="text-sm text-gray-500 font-medium text-center">{card.title}</p>
                            <p className="text-2xl font-bold text-text-primary text-center">{card.value}</p>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <a
                        href="#"
                        onClick={() => setActiveTab('store')}
                        className={`
                            whitespace-nowrap px-1 py-4 text-sm font-medium flex items-center gap-2
                            ${activeTab === 'store' ? 'border-indigo-500 text-indigo-600 border-b-2' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                        `}
                    >
                        <Info size={18} /> Store & Owner Info
                    </a>
                    <a
                        href="#"
                        onClick={() => setActiveTab('staff')}
                        className={`
                            whitespace-nowrap px-1 py-4 text-sm font-medium flex items-center gap-2
                            ${activeTab === 'staff' ? 'border-indigo-500 text-indigo-600 border-b-2' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                        `}
                    >
                        <Users size={18} /> Staff
                    </a>
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'store' && (
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary">Branch & Ownership Details</h2>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700">
                        <div className="flex flex-col">
                            <dt className="font-medium text-gray-500 flex items-center gap-2"><MapPin size={16} /> Full Address</dt>
                            <dd className="font-semibold text-text-primary">{`${branch.address?.address1 || 'N/A'}, ${branch.address?.city || 'N/A'}, ${branch.address?.state || 'N/A'} ${branch.address?.zipcode || 'N/A'}, ${branch.address?.country || 'N/A'}`}</dd>
                        </div>
                        <div className="flex flex-col">
                            <dt className="font-medium text-gray-500 flex items-center gap-2"><Phone size={16} /> Phone Number</dt>
                            <dd className="font-semibold text-text-primary">{branch.address?.phone1 || 'N/A'}</dd>
                        </div>
                        <div className="flex flex-col">
                            <dt className="font-medium text-gray-500 flex items-center gap-2"><User size={16} /> Owner Name</dt>
                            <dd className="font-semibold text-text-primary">{branch.ownerName || 'N/A'}</dd>
                        </div>
                        <div className="flex flex-col">
                            <dt className="font-medium text-gray-500 flex items-center gap-2"><Mail size={16} /> Owner Email</dt>
                            <dd className="font-semibold text-text-primary">{branch.owner?.user?.credential?.logon_id || 'N/A'}</dd>
                        </div>
                        <div className="flex flex-col">
                            <dt className="font-medium text-gray-500 flex items-center gap-2"><Banknote size={16} /> Default Currency</dt>
                            <dd className="font-semibold text-text-primary">{`${branch.setccurr} (${currencyCodeToSymbolMap[branch.setccurr] || branch.setccurr})`}</dd>
                        </div>
                    </dl>
                </Card>
            )}

            {activeTab === 'staff' && (
                <Card className="p-6">
                    <h2 className="text-xl font-semibold mb-4 text-text-primary flex items-center gap-2">
                        <Users size={20} /> Store Staff
                    </h2>
                    <p className="mb-4 text-sm text-gray-600">
                        A directory of all staff members and their roles at this branch.
                    </p>
                    <DataTable2
                        columns={staffColumns}
                        data={branch.staff || []}
                        rowsPerPage={10}
                        emptyMessage="No staff members assigned to this branch."
                    />
                </Card>
            )}
        </div>
    );
};

export default StoreDetails;